"use strict";
import { TOPAZ_STATS_API_ENABLED } from "api/topazStats/client";
import { UniverseChainId } from "features/chains/types";
import { useMemo } from "react";
import { useLiveGaugeAprMap } from "./useLiveGaugeAprMap";
import { useTopazLiveDynamicFees } from "./useTopazLiveDynamicFees";
import { useTopazStatsGauges } from "./useTopazStatsGauges";
import { useTopazStatsPools } from "./useTopazStatsPools";
const EMPTY_POOL_TO_GAUGE = /* @__PURE__ */ new Map();
export function statsFeeToPips(pool) {
  if (pool.fee === null || pool.fee === void 0) {
    return void 0;
  }
  return pool.poolType.startsWith("v2") ? pool.fee * 100 : pool.fee;
}
export function statsGaugeAprPercent(pool) {
  if (!pool) {
    return 0;
  }
  const apr = typeof pool.gaugeApr === "string" ? parseFloat(pool.gaugeApr) : NaN;
  return Number.isFinite(apr) ? apr : 0;
}
export function statsFeeAprPercent(pool) {
  if (!pool) {
    return 0;
  }
  const apr = typeof pool.feeApr === "string" ? parseFloat(pool.feeApr) : NaN;
  return Number.isFinite(apr) ? apr : 0;
}
function finiteFee(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : void 0;
}
export function buildPoolFeeDetails(pool, liveFeePool) {
  if (!pool && !liveFeePool) {
    return void 0;
  }
  const snapshotFeePips = finiteFee(liveFeePool?.snapshotFee) ?? (pool ? statsFeeToPips(pool) : void 0);
  const currentFeePips = finiteFee(liveFeePool?.liveFee) ?? snapshotFeePips;
  return {
    currentFeePips,
    snapshotFeePips,
    baseFeePips: finiteFee(liveFeePool?.baseFee),
    maxFeePips: finiteFee(liveFeePool?.maxFee) ?? finiteFee(pool?.dynamicFeeCap),
    customFee: liveFeePool?.customFee ?? pool?.customFee ?? false,
    dynamicFee: liveFeePool?.dynamicFee ?? pool?.dynamicFee ?? false
  };
}
export function usePoolTableEnrichment(chainId, enabled = true) {
  const apiEnabled = TOPAZ_STATS_API_ENABLED && enabled && chainId === UniverseChainId.Mainnet;
  const pools = useTopazStatsPools(chainId, enabled);
  const gauges = useTopazStatsGauges(chainId, enabled);
  const liveFees = useTopazLiveDynamicFees(chainId, enabled);
  const liveApr = useLiveGaugeAprMap(chainId, enabled);
  const { poolsByAddress, isSuccess: poolsSuccess, isLoading: poolsLoading } = pools;
  const { poolToGaugeMap: apiPoolToGaugeMap, isSuccess: gaugesSuccess, isLoading: gaugesLoading } = gauges;
  const { liveFeesByAddress } = liveFees;
  const { aprByPool: liveAprByPool, isReady: liveAprReady } = liveApr;
  return useMemo(() => {
    const usingApi = apiEnabled && poolsSuccess && gaugesSuccess && poolsByAddress.size > 0;
    const getFeeDetails = (poolId) => {
      const key = poolId.toLowerCase();
      return buildPoolFeeDetails(poolsByAddress.get(key), liveFeesByAddress.get(key));
    };
    const getFeePips = (poolId) => {
      return getFeeDetails(poolId)?.currentFeePips;
    };
    const hasDynamicFee = (poolId) => getFeeDetails(poolId)?.dynamicFee ?? false;
    const getGaugeAprPercent = (poolId) => {
      const key = poolId.toLowerCase();
      if (liveAprReady) {
        const liveApr2 = liveAprByPool.get(key);
        if (liveApr2 !== void 0 && Number.isFinite(liveApr2)) {
          return liveApr2;
        }
      }
      return statsGaugeAprPercent(poolsByAddress.get(key));
    };
    const getFeeAprPercent = (poolId) => {
      return statsFeeAprPercent(poolsByAddress.get(poolId.toLowerCase()));
    };
    return {
      poolToGaugeMap: usingApi ? apiPoolToGaugeMap : EMPTY_POOL_TO_GAUGE,
      usingApi,
      isLoading: apiEnabled && (poolsLoading || gaugesLoading),
      getFeePips,
      getFeeDetails,
      hasDynamicFee,
      getGaugeAprPercent,
      getFeeAprPercent
    };
  }, [
    apiEnabled,
    poolsByAddress,
    liveFeesByAddress,
    apiPoolToGaugeMap,
    liveAprByPool,
    liveAprReady,
    poolsSuccess,
    gaugesSuccess,
    poolsLoading,
    gaugesLoading
  ]);
}
